import { createAsyncThunk } from '@reduxjs/toolkit';
import { getFilterConfiguration, getFacetData } from '../services/filterService';
import { setAvailableFilters, setFacetData, clearFacetData, setLoading, setError } from './filterSlice';

export const loadFilterConfiguration = createAsyncThunk(
  'filters/loadConfiguration',
  async ({ projectId, forceRefresh = false }, { dispatch, getState, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));

      const filterableFields = await getFilterConfiguration(projectId);
      dispatch(setAvailableFilters(filterableFields));

      // Check if facets are already loaded
      const state = getState();
      const facetDataLoaded = state.filters.facetDataLoaded;

      // Get facets for fields with facet: true (but not date fields)
      const fieldsNeedingFacets = filterableFields
        .filter(field =>
          field.facet === true &&
          field.type !== 'pdate' &&  // pdate is date field type
          field.type !== 'date'
        )
        .map(field => field.value);  // Use 'value' instead of 'key'

      // Only load facets if not already loaded or if force refresh
      if (fieldsNeedingFacets.length > 0 && (!facetDataLoaded || forceRefresh)) {
        console.log(forceRefresh ? '🔄 Force refreshing facet data...' : '📊 Loading facet data for first time...');
        const facetData = await getFacetData(projectId, fieldsNeedingFacets);
        dispatch(setFacetData(facetData));
      } else if (facetDataLoaded) {
        console.log('✅ Facet data already loaded, using cached version');
      }

      return filterableFields;
    } catch (error) {
      const errorMessage = error.message || 'Failed to load filter configuration';
      dispatch(setError(errorMessage));
      return rejectWithValue(errorMessage);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const loadFacetData = createAsyncThunk(
  'filters/loadFacetData',
  async ({ projectId, fieldNames }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));

      const facetData = await getFacetData(projectId, fieldNames);
      dispatch(setFacetData(facetData));

      return facetData;
    } catch (error) {
      const errorMessage = error.message || 'Failed to load facet data';
      dispatch(setError(errorMessage));
      return rejectWithValue(errorMessage);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const refreshFacetData = createAsyncThunk(
  'filters/refreshFacetData',
  async (projectId, { dispatch, getState, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));

      const state = getState();
      const availableFilters = state.filters.availableFilters;

      // Get fields that need facets
      const fieldsNeedingFacets = availableFilters
        .filter(field =>
          field.facet === true &&
          field.type !== 'pdate' &&
          field.type !== 'date'
        )
        .map(field => field.value);

      if (fieldsNeedingFacets.length > 0) {
        console.log('🔄 Refreshing facet data...');
        // Clear existing facet data first
        dispatch(clearFacetData());

        const facetData = await getFacetData(projectId, fieldsNeedingFacets);
        dispatch(setFacetData(facetData));
        return facetData;
      }

      return {};
    } catch (error) {
      const errorMessage = error.message || 'Failed to refresh facet data';
      dispatch(setError(errorMessage));
      return rejectWithValue(errorMessage);
    } finally {
      dispatch(setLoading(false));
    }
  }
);